import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Image as ImageIcon, Upload, Download, Youtube, CalendarPlus, Sparkles, Volume2, Play, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import Stepper from "@/components/Stepper";
import { PageContainer, PageHeader } from "@/components/Page";

const videoTypes = ["Narrative/Story", "Tutorial", "Product Showcase", "Explainer", "Ad/Promo"];
const tones = ["Dramatic", "Professional", "Fun", "Inspirational", "Dark/Suspenseful"];
const durations = ["30s", "1 min", "3 min", "5 min", "10 min"];
const platformsList = [
  { id: "YouTube", aspect: "16:9", res: "1920×1080" },
  { id: "YouTube Shorts", aspect: "9:16", res: "1080×1920" },
  { id: "Instagram Reels", aspect: "9:16", res: "1080×1920" },
  { id: "Instagram Feed", aspect: "1:1", res: "1080×1080" },
  { id: "TikTok", aspect: "9:16", res: "1080×1920" },
  { id: "Facebook", aspect: "16:9", res: "1920×1080" },
  { id: "Facebook Stories", aspect: "9:16", res: "1080×1920" },
  { id: "LinkedIn", aspect: "16:9", res: "1920×1080" },
];

const VFNew = () => {
  const { videoId } = useParams();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  // Step 1
  const [topic, setTopic] = useState("");
  const [vt, setVt] = useState("Narrative/Story");
  const [tn, setTn] = useState("Professional");
  const [dur, setDur] = useState("1 min");
  const [plats, setPlats] = useState(["YouTube"]);
  const [autoDim, setAutoDim] = useState(true);
  // Step 3 controls
  const [captionStyle, setCaptionStyle] = useState("Subtitle bar");
  const [transition, setTransition] = useState("Crossfade");
  const [quality, setQuality] = useState("720p");
  // Step 2
  const [voices, setVoices] = useState([]);
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const audioRef = React.useRef(null);
  const [selectedVoice, setSelectedVoice] = useState(null);
  // Step 4
  const [previewAspect, setPreviewAspect] = useState("16:9");
  const [thumbIdx, setThumbIdx] = useState(0);

  useEffect(() => {
    if (videoId) {
      api.get(`/video-factory/${videoId}`).then(({ data }) => {
        setVideo(data); setStep(Math.max(0, (data.step || 1) - 1));
        if (data.platforms) setPlats(data.platforms);
      }).catch(() => {});
    }
  }, [videoId]);

  // Poll while rendering
  useEffect(() => {
    if (!video || video.status !== "rendering") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/video-factory/${video.id}`);
        setVideo(data);
        if (data.status !== "rendering") {
          clearInterval(t);
          if (data.status === "ready") toast.success("Video rendered for all aspects!");
          else if (data.status === "failed") toast.error("Render failed: " + (data.render_error || ""));
        }
      } catch (e) {}
    }, 4000);
    return () => clearInterval(t);
  }, [video?.status, video?.id]);

  useEffect(() => {
    if (step === 1 && voices.length === 0) {
      api.get("/video-factory/voices/list").then(({ data }) => setVoices(data.voices)).catch(() => {});
    }
  }, [step]);

  const togglePlatform = (p) => {
    setPlats((cur) => cur.includes(p) ? cur.filter(x => x !== p) : [...cur, p]);
  };

  const createVideo = async () => {
    if (!topic.trim()) return toast.error("Enter a topic");
    setLoading(true);
    try {
      const { data } = await api.post("/video-factory/create", { topic, video_type: vt, tone: tn, duration: dur, platforms: plats, auto_dimensions: autoDim });
      setVideo(data);
      nav(`/video-factory/new/${data.id}`, { replace: true });
      // Auto-generate script
      const r = await api.post("/video-factory/script", { video_id: data.id });
      setVideo(r.data);
      setStep(1);
      toast.success("Script generated!");
    } catch (e) { toast.error("Failed to create"); }
    finally { setLoading(false); }
  };

  const regenScript = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/video-factory/script", { video_id: video.id, regenerate: true });
      setVideo(data);
      toast.success("Script regenerated");
    } finally { setLoading(false); }
  };

  const genVoice = async () => {
    if (!selectedVoice) return toast.error("Pick a voice");
    setLoading(true);
    try {
      const { data } = await api.post("/video-factory/voice", { video_id: video.id, voice_id: selectedVoice });
      setVideo(data);
      setStep(2);
      toast.success("Voiceover generated");
    } catch (e) { toast.error("Voice generation failed"); }
    finally { setLoading(false); }
  };

  const regenScene = async (scene_id, source = "ai") => {
    try {
      const { data } = await api.post("/video-factory/scene/regenerate", { video_id: video.id, scene_id, source });
      setVideo((v) => ({ ...v, scenes: v.scenes.map((s) => s.id === scene_id ? data.scene : s) }));
      toast.success(`Scene ${source === 'stock' ? 'swapped with stock' : 'regenerated'}`);
    } catch (e) { toast.error("Failed"); }
  };

  const [genAllLoading, setGenAllLoading] = useState(false);
  const [genAllProgress, setGenAllProgress] = useState("");
  const generateAllAI = async () => {
    if (!video) return;
    if (!confirm(`This will fire AI generation for all ${(video.scenes || []).length} scenes in parallel (~1-2 min). Continue?`)) return;
    setGenAllLoading(true); setGenAllProgress(`0/${(video.scenes || []).length}`);
    try {
      const { data } = await api.post("/video-factory/generate-all-ai", { video_id: video.id });
      setVideo((v) => ({ ...v, scenes: data.scenes }));
      toast.success(`AI generated ${data.ai_succeeded}/${data.total} scenes`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally {
      setGenAllLoading(false);
      setGenAllProgress("");
    }
  };

  const assemble = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/video-factory/assemble", {
        video_id: video.id,
        caption_style: captionStyle,
        transition: transition,
        quality: quality,
      });
      setVideo(data);
      setStep(3);
      toast.info("Rendering started — this may take 1-3 minutes for all aspects");
    } finally { setLoading(false); }
  };

  const publish = async (target) => {
    try {
      await api.post("/video-factory/publish", { video_id: video.id, platforms: [target] });
      toast.success(`Published to ${target} (mocked)`);
    } catch (e) {}
  };

  const stepLabels = ["Configure", "Script & Voice", "Visuals", "Review"];

  const handleDiscardDraft = async () => {
    if (!video?.id) return;
    if (!confirm("Discard this draft? All generated content for this video will be permanently deleted.")) return;
    try {
      await api.delete(`/video-factory/${video.id}`);
      toast.success("Draft discarded");
      nav("/video-factory/history");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete draft");
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="AI Video Factory"
        subtitle="From topic to publish-ready in 4 steps."
        testId="vf-new-header"
        actions={video?.id ? (
          <button
            type="button"
            onClick={handleDiscardDraft}
            className="op-btn-secondary text-red-600 hover:border-red-300"
            data-testid="vf-discard-draft"
          >
            <Trash2 size={16} /> Discard Draft
          </button>
        ) : null}
      />
      <Stepper steps={stepLabels} current={step} />

      {step === 0 && (
        <div className="max-w-3xl mx-auto" data-testid="vf-step-configure">
          <div className="op-card p-6 mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Topic / Brief</label>
            <textarea rows={3} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Behind the scenes of our coffee roastery in Brooklyn..." className="op-input" data-testid="vf-topic-input" />
          </div>
          <div className="op-card p-6 mb-5">
            <h3 className="font-semibold text-gray-900 mb-3">Video type</h3>
            <div className="flex flex-wrap gap-2">{videoTypes.map((t) => <button key={t} onClick={() => setVt(t)} className={vt === t ? "op-pill-active" : "op-pill-inactive"} data-testid={`vt-${t}`}>{t}</button>)}</div>
          </div>
          <div className="op-card p-6 mb-5">
            <h3 className="font-semibold text-gray-900 mb-3">Tone</h3>
            <div className="flex flex-wrap gap-2">{tones.map((t) => <button key={t} onClick={() => setTn(t)} className={tn === t ? "op-pill-active" : "op-pill-inactive"}>{t}</button>)}</div>
          </div>
          <div className="op-card p-6 mb-5">
            <h3 className="font-semibold text-gray-900 mb-3">Duration</h3>
            <div className="flex flex-wrap gap-2">{durations.map((d) => <button key={d} onClick={() => setDur(d)} className={dur === d ? "op-pill-active" : "op-pill-inactive"}>{d}</button>)}</div>
          </div>
          <div className="op-card p-6 mb-5">
            <h3 className="font-semibold text-gray-900 mb-3">Target platforms</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {platformsList.map((p) => {
                const active = plats.includes(p.id);
                return (
                  <button key={p.id} onClick={() => togglePlatform(p.id)} className={`op-card p-3 text-center transition-all ${active ? 'border-[#7C3AED] ring-2 ring-purple-200 bg-purple-50 scale-[1.02]' : ''}`} data-testid={`platform-${p.id}`}>
                    <p className="text-sm font-semibold text-gray-900">{p.id}</p>
                    <p className="text-xs text-gray-500 mt-1">{p.aspect} · {p.res}</p>
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 mt-4 text-sm">
              <input type="checkbox" checked={autoDim} onChange={(e) => setAutoDim(e.target.checked)} className="rounded text-[#7C3AED]" />
              Generate all dimensions automatically
            </label>
          </div>
          <button onClick={createVideo} disabled={loading} className="op-btn-primary mx-auto" data-testid="vf-create-btn">
            <Sparkles size={16} /> {loading ? "Generating..." : "Generate Script →"}
          </button>
        </div>
      )}

      {step === 1 && video && (
        <div className="max-w-4xl mx-auto" data-testid="vf-step-script">
          <div className="op-card p-6 mb-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-900">Script</h3>
              <button onClick={regenScript} className="op-btn-secondary text-sm py-2" disabled={loading}><RefreshCw size={14} /> Regenerate</button>
            </div>
            <div className="space-y-4 text-sm">
              <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Hook</p><p className="text-gray-800">{video.script?.hook}</p></div>
              <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Content</p><p className="text-gray-800 whitespace-pre-wrap">{video.script?.content}</p></div>
              <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">CTA</p><p className="text-gray-800">{video.script?.cta}</p></div>
            </div>
          </div>
          <div className="op-card p-6 mb-5">
            <h3 className="font-bold text-gray-900 mb-4">SEO Metadata</h3>
            <div className="space-y-3">
              <div><label className="text-xs text-gray-500">Title</label><p className="font-medium">{video.title}</p></div>
              <div><label className="text-xs text-gray-500">Description</label><p className="text-sm">{video.description}</p></div>
              <div><label className="text-xs text-gray-500">Tags</label><div className="flex flex-wrap gap-1 mt-1">{(video.tags || []).map((t) => <span key={t} className="px-2 py-0.5 bg-purple-100 text-[#7C3AED] rounded text-xs">{t}</span>)}</div></div>
            </div>
          </div>
          <div className="op-card p-6 mb-5">
            <h3 className="font-bold text-gray-900 mb-1">Pick a voice</h3>
            <p className="text-xs text-gray-500 mb-4">Click ▶ to preview each voice (~3 seconds)</p>
            <audio ref={audioRef} onEnded={() => setPreviewingVoice(null)} className="hidden" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {voices.map((v) => {
                const isSel = selectedVoice === v.voice_id;
                const isPlaying = previewingVoice === v.voice_id;
                const playPreview = (e) => {
                  e.stopPropagation();
                  if (!audioRef.current || !v.preview) return;
                  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${v.preview}`;
                  if (isPlaying) {
                    audioRef.current.pause();
                    setPreviewingVoice(null);
                    return;
                  }
                  audioRef.current.src = fullUrl;
                  audioRef.current.play().catch(() => setPreviewingVoice(null));
                  setPreviewingVoice(v.voice_id);
                };
                return (
                  <button key={v.voice_id} onClick={() => setSelectedVoice(v.voice_id)} className={`op-card p-4 text-left transition-all relative ${isSel ? 'border-[#7C3AED] ring-2 ring-purple-200 bg-purple-50' : ''}`} data-testid={`voice-${v.voice_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Volume2 size={14} className="text-[#7C3AED] flex-shrink-0" />
                        <p className="font-semibold text-sm truncate">{v.name}</p>
                      </div>
                      <span onClick={playPreview} className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition ${isPlaying ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 text-gray-700 hover:bg-purple-100 hover:text-[#7C3AED]'}`} data-testid={`voice-preview-${v.voice_id}`} title="Preview voice">
                        {isPlaying ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} fill="currentColor" />}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{v.description || v.category}</p>
                    {v.labels?.gender && <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mt-1 inline-block">{v.labels.gender}</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <button onClick={genVoice} disabled={loading || !selectedVoice} className="op-btn-primary mx-auto" data-testid="vf-gen-voice">
            <Sparkles size={16} /> {loading ? "Generating voice..." : "Generate Voice →"}
          </button>
        </div>
      )}

      {step === 2 && video && (
        <div className="max-w-5xl mx-auto" data-testid="vf-step-visuals">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Scenes ({(video.scenes || []).length})</h3>
            <button onClick={generateAllAI} disabled={genAllLoading} className="op-btn-primary flex items-center gap-2" data-testid="vf-generate-all-ai">
              {genAllLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {genAllLoading ? `Generating ${genAllProgress}...` : "✨ Generate ALL with AI"}
            </button>
          </div>
          <div className="space-y-4 mb-6">
            {(video.scenes || []).map((s, i) => {
              const previewUrl = s.video_url
                ? (s.video_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${s.video_url}` : s.video_url)
                : null;
              const isVideo = previewUrl && (previewUrl.endsWith(".mp4") || previewUrl.includes("/video/"));
              const fileInputId = `scene-upload-${s.id}`;
              const onUploadFile = async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append("video_id", video.id);
                fd.append("scene_id", s.id);
                fd.append("file", f);
                try {
                  const { data } = await api.post("/video-factory/scene/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  setVideo((v) => ({ ...v, scenes: v.scenes.map((sc) => sc.id === s.id ? data.scene : sc) }));
                  toast.success(`Scene ${i + 1} uploaded`);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Upload failed");
                }
                e.target.value = "";
              };
              return (
                <div key={s.id} className="op-card p-4 flex gap-4" data-testid={`scene-${i}`}>
                  <div className="w-40 aspect-video bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {previewUrl ? (
                      isVideo ? <video key={previewUrl} src={previewUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" /> : <img src={previewUrl} alt="" className="w-full h-full object-cover" />
                    ) : <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon size={28} /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Scene {i + 1} <span className="text-[10px] text-gray-400 ml-1 normal-case">· {s.source || "stock"}</span></p>
                    <p className="text-sm text-gray-800 mb-2">{s.text}</p>
                    <p className="text-xs text-gray-500 italic mb-3">Visual: {s.visual_prompt}</p>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => regenScene(s.id, "ai")} className="op-btn-secondary text-xs py-1.5" data-testid={`scene-${i}-ai`}><RefreshCw size={12} /> Regen AI</button>
                      <button onClick={() => regenScene(s.id, "stock")} className="op-btn-secondary text-xs py-1.5" data-testid={`scene-${i}-stock`}>Swap Stock</button>
                      <input id={fileInputId} type="file" accept="video/*,image/*" className="hidden" onChange={onUploadFile} data-testid={`scene-${i}-upload-input`} />
                      <label htmlFor={fileInputId} className="op-btn-secondary text-xs py-1.5 cursor-pointer inline-flex items-center gap-1" data-testid={`scene-${i}-upload`}><Upload size={12} /> Upload</label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="op-card p-5 mb-6" data-testid="vf-render-options">
            <h3 className="font-bold text-gray-900 mb-4">Render options</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Caption style</label>
                <div className="flex flex-wrap gap-2">
                  {["Subtitle bar", "Word highlight", "None"].map((c) => (
                    <button key={c} onClick={() => setCaptionStyle(c)} className={captionStyle === c ? "op-pill-active" : "op-pill-inactive"} data-testid={`caption-${c.replace(/\s/g,'-').toLowerCase()}`}>{c}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Transition</label>
                <div className="flex flex-wrap gap-2">
                  {["Cut", "Crossfade", "Zoom"].map((t) => (
                    <button key={t} onClick={() => setTransition(t)} className={transition === t ? "op-pill-active" : "op-pill-inactive"} data-testid={`transition-${t.toLowerCase()}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Quality</label>
                <div className="flex gap-2">
                  {["720p", "1080p"].map((q) => (
                    <button key={q} onClick={() => setQuality(q)} className={quality === q ? "op-pill-active" : "op-pill-inactive"} data-testid={`quality-${q}`}>{q}</button>
                  ))}
                </div>
                {quality === "1080p" && <p className="text-xs text-gray-500 mt-2">Higher quality renders take ~2× longer.</p>}
              </div>
            </div>
          </div>
          <button onClick={assemble} disabled={loading} className="op-btn-primary mx-auto" data-testid="vf-assemble">
            <Sparkles size={16} /> {loading ? "Assembling..." : "Assemble Video →"}
          </button>
        </div>
      )}

      {step === 3 && video && (
        <div className="max-w-4xl mx-auto" data-testid="vf-step-review">
          {video.status === "rendering" && (
            <div className="op-card p-8 text-center mb-5" data-testid="render-progress">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#EDE9FE] flex items-center justify-center mb-4">
                <Sparkles className="text-[#7C3AED] animate-spin" size={26} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Rendering your video</h3>
              <p className="text-gray-500 text-sm">Compositing scenes, adding voiceover and captions across every platform aspect. This usually takes 1-3 minutes.</p>
              <div className="mt-5 max-w-md mx-auto h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] animate-shimmer" style={{ backgroundSize: "200% 100%", width: "60%" }} /></div>
            </div>
          )}
          {video.status === "failed" && (
            <div className="op-card p-6 mb-5 border-red-200 bg-red-50" data-testid="render-failed">
              <p className="font-semibold text-red-700">Rendering failed</p>
              <p className="text-sm text-red-600 mt-1">{video.render_error || "Unknown error"}</p>
              <button onClick={assemble} className="op-btn-primary mt-4">Retry →</button>
            </div>
          )}
          {(video.status === "ready" || video.status === "published") && (() => {
            const renders = video.renders || {};
            const availableAspects = Object.keys(renders).length ? Object.keys(renders) : ["16:9"];
            const aspect = availableAspects.includes(previewAspect) ? previewAspect : availableAspects[0];
            const currentUrl = renders[aspect] || video.video_url;
            const fullUrl = currentUrl?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${currentUrl}` : currentUrl;
            return (
              <>
                <div className="flex gap-2 mb-4 flex-wrap" data-testid="aspect-tabs">
                  {availableAspects.map((a) => (
                    <button key={a} onClick={() => setPreviewAspect(a)} className={aspect === a ? "op-pill-active" : "op-pill-inactive"} data-testid={`aspect-${a}`}>{a}</button>
                  ))}
                </div>
                <div className="op-card overflow-hidden mb-5 mx-auto bg-black" style={{ aspectRatio: aspect.replace(":", "/"), maxWidth: aspect === "9:16" ? "360px" : aspect === "1:1" ? "500px" : "100%" }} data-testid="video-preview">
                  {fullUrl ? <video key={fullUrl} src={fullUrl} controls className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-200" />}
                </div>
              </>
            );
          })()}

          <div className="op-card p-5 mb-5">
            <p className="text-sm font-semibold mb-3">Pick a thumbnail</p>
            <div className="grid grid-cols-3 gap-3">
              {(video.thumbnails || []).map((t, i) => (
                <button key={i} onClick={() => setThumbIdx(i)} className={`aspect-video rounded-lg overflow-hidden border-2 ${thumbIdx === i ? 'border-[#7C3AED]' : 'border-transparent'}`}>
                  <img src={t} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
          <div className="op-card p-5 mb-5">
            <p className="text-sm font-semibold mb-3">Metadata (auto-filled)</p>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-500">Title:</span> {video.title}</p>
              <p><span className="text-gray-500">Description:</span> {video.description}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => publish("YouTube")} disabled={video.status !== "ready" && video.status !== "published"} className="op-btn-primary" data-testid="vf-publish-youtube"><Youtube size={16} /> Upload to YouTube</button>
            <button onClick={() => publish("Calendar")} className="op-btn-secondary"><CalendarPlus size={16} /> Schedule</button>
            <button onClick={() => publish("Social")} className="op-btn-secondary">Post to Social</button>
            <button onClick={() => {
              const renders = video.renders || {};
              Object.entries(renders).forEach(([a, url]) => {
                const fullUrl = url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
                const link = document.createElement("a");
                link.href = fullUrl;
                link.download = `${video.title || video.topic}_${a.replace(":","x")}.mp4`;
                link.click();
              });
            }} disabled={!Object.keys(video.renders || {}).length} className="op-btn-secondary" data-testid="vf-download-all"><Download size={16} /> Download All</button>
          </div>
        </div>
      )}
    </PageContainer>
  );
};
export default VFNew;
